import React from "react";
import { Box, Button, Chip, MenuItem, TextField, Typography } from "@mui/material";
import { Coffee, LogIn, LogOut, Play } from "lucide-react";
import { breakLabel, formatClock, formatTime } from "../../../utils/attendance";
import { AGENT_INK, AGENT_LINE, AGENT_MUTED } from "../../../agentComponents/agentTheme";

// The clock face: a progress ring around the running timer, the way Keka shows
// it. The ring fills against the rostered shift length so an agent can read
// "how much of my day is done" at a glance instead of doing arithmetic.

const RING = 188;
const STROKE = 12;
const RADIUS = (RING - STROKE) / 2;
const CIRC = 2 * Math.PI * RADIUS;

/** Rostered shift length in ms, or null when the agent has no shift set. */
const shiftLengthMs = (agent) => {
  const parse = (v) => {
    if (!/^\d{1,2}:\d{2}$/.test(v || "")) return null;
    const [h, m] = v.split(":").map(Number);
    return h * 60 + m;
  };
  const start = parse(agent?.shiftStart);
  const end = parse(agent?.shiftEnd);
  if (start === null || end === null) return null;
  // An end before the start means the shift runs past midnight.
  const minutes = end > start ? end - start : 24 * 60 - start + end;
  return minutes * 60000;
};

export default function ClockCard({
  state,
  current,
  live,
  agent,
  breakTypes,
  breakType,
  setBreakType,
  breakNote,
  setBreakNote,
  busy,
  onClockIn,
  onClockOut,
  onBreakStart,
  onBreakEnd,
}) {
  const onBreak = state === "on_break";
  const clockedIn = state === "clocked_in";
  const off = state === "off";

  const selectedType = breakTypes.find((t) => t.key === breakType);
  const cap = current?.currentBreak?.maxMinutes || 0;
  const overrun = onBreak && cap > 0 && live.currentBreakMs > cap * 60000;

  const target = shiftLengthMs(agent) || 8 * 3600000;
  const progress = Math.min(1, live.productiveMs / target);

  const ringColor = overrun ? "#DC2626" : onBreak ? "#F59E0B" : clockedIn ? "#16A34A" : "#CBD5E1";
  const displayed = onBreak ? live.currentBreakMs : live.productiveMs;

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: "16px",
        border: `1px solid ${AGENT_LINE}`,
        p: 3,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: AGENT_INK }}>Web Clock-In</Typography>
        {agent?.weekOff && (
          <Chip
            size="small"
            label="Week off today"
            sx={{ bgcolor: "#FEF2F2", color: "#DC2626", fontWeight: 600, fontSize: 11.5 }}
          />
        )}
        {agent?.shiftStart && (
          <Chip
            size="small"
            label={`${agent.shiftStart} - ${agent.shiftEnd || "--"}`}
            sx={{ bgcolor: "#F1F5F9", color: AGENT_MUTED, fontWeight: 600, fontSize: 11.5 }}
          />
        )}
      </Box>

      {/* ── Ring ─────────────────────────────────────────────────────────── */}
      <Box sx={{ position: "relative", width: RING, height: RING, my: 1 }}>
        <svg width={RING} height={RING} style={{ transform: "rotate(-90deg)" }}>
          <circle
            cx={RING / 2}
            cy={RING / 2}
            r={RADIUS}
            fill="none"
            stroke="#EEF2F7"
            strokeWidth={STROKE}
          />
          <circle
            cx={RING / 2}
            cy={RING / 2}
            r={RADIUS}
            fill="none"
            stroke={ringColor}
            strokeWidth={STROKE}
            strokeLinecap="round"
            strokeDasharray={CIRC}
            strokeDashoffset={CIRC * (1 - progress)}
            style={{ transition: "stroke-dashoffset .6s ease, stroke .3s ease" }}
          />
        </svg>

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: 30,
              fontWeight: 700,
              color: overrun ? "#DC2626" : AGENT_INK,
              lineHeight: 1.1,
            }}
          >
            {formatClock(displayed)}
          </Typography>
          <Typography sx={{ fontSize: 12, color: AGENT_MUTED, mt: 0.5, px: 2, textAlign: "center" }}>
            {off
              ? "Not clocked in"
              : onBreak
              ? overrun
                ? `Over your ${cap} min break`
                : breakLabel(current.currentBreak)
              : "Effective hours today"}
          </Typography>
          {current && !onBreak && (
            <Typography sx={{ fontSize: 11.5, color: "#94A3B8", mt: 0.25 }}>
              since {formatTime(current.clockInAt)}
            </Typography>
          )}
        </Box>
      </Box>

      {current?.lateBy > 0 && (
        <Chip
          size="small"
          label="Late arrival"
          sx={{ mt: 1, bgcolor: "#FEF2F2", color: "#DC2626", fontWeight: 700, fontSize: 11 }}
        />
      )}

      {/* ── Actions ──────────────────────────────────────────────────────── */}
      <Box sx={{ width: "100%", mt: 2.5, display: "flex", flexDirection: "column", gap: 1.25 }}>
        {off ? (
          <Button
            fullWidth
            size="large"
            variant="contained"
            disableElevation
            startIcon={<LogIn size={18} />}
            disabled={busy === "in"}
            onClick={onClockIn}
            sx={{
              py: 1.35,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: 15,
              bgcolor: "#16A34A",
              "&:hover": { bgcolor: "#15803D" },
            }}
          >
            {busy === "in" ? "Clocking in..." : "Clock In"}
          </Button>
        ) : (
          <>
            {onBreak ? (
              <Button
                fullWidth
                size="large"
                variant="contained"
                disableElevation
                startIcon={<Play size={18} />}
                disabled={busy === "breakEnd"}
                onClick={onBreakEnd}
                sx={{
                  py: 1.3,
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                  bgcolor: "#F59E0B",
                  "&:hover": { bgcolor: "#D97706" },
                }}
              >
                {busy === "breakEnd" ? "Resuming..." : "Resume Work"}
              </Button>
            ) : (
              <>
                {/* With no catalogue there is nothing to pick, and a disabled
                    select cannot be opened to explain itself -- so say it
                    inline rather than leaving a dead dropdown on the card. */}
                {!breakTypes.length ? (
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderRadius: "10px",
                      bgcolor: "#F8FAFC",
                      border: `1px dashed ${AGENT_LINE}`,
                      textAlign: "center",
                    }}
                  >
                    <Typography sx={{ fontSize: 12.5, color: AGENT_MUTED }}>
                      No break types set up yet. Ask your admin to add one.
                    </Typography>
                  </Box>
                ) : (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    select
                    size="small"
                    value={breakType}
                    onChange={(e) => setBreakType(e.target.value)}
                    sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                  >
                    {breakTypes.map((t) => (
                      <MenuItem key={t.key} value={t.key}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box
                            sx={{
                              width: 9,
                              height: 9,
                              borderRadius: "50%",
                              bgcolor: t.color || "#94A3B8",
                              flexShrink: 0,
                            }}
                          />
                          <Typography sx={{ fontSize: 14 }}>{t.label}</Typography>
                          {t.maxMinutes > 0 && (
                            <Typography sx={{ fontSize: 11.5, color: "#94A3B8" }}>
                              {t.maxMinutes}m
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button
                    variant="outlined"
                    startIcon={<Coffee size={17} />}
                    disabled={busy === "breakStart" || !breakType}
                    onClick={onBreakStart}
                    sx={{
                      borderRadius: "10px",
                      textTransform: "none",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      borderColor: "#FCD9A4",
                      color: "#B45309",
                      bgcolor: "#FFFBEB",
                      "&:hover": { borderColor: "#F59E0B", bgcolor: "#FEF3C7" },
                    }}
                  >
                    Break
                  </Button>
                </Box>
                )}

                {breakTypes.length > 0 && (
                  <TextField
                    size="small"
                    placeholder="Break note (optional)"
                    value={breakNote}
                    onChange={(e) => setBreakNote(e.target.value)}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                  />
                )}
                {selectedType?.maxMinutes > 0 && (
                  <Typography sx={{ fontSize: 11.5, color: AGENT_MUTED, mt: -0.5 }}>
                    {selectedType.label} is capped at {selectedType.maxMinutes} minutes.
                  </Typography>
                )}
              </>
            )}

            <Button
              fullWidth
              size="large"
              variant="outlined"
              startIcon={<LogOut size={18} />}
              disabled={busy === "out"}
              onClick={onClockOut}
              sx={{
                py: 1.2,
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                borderColor: "#FCA5A5",
                color: "#DC2626",
                "&:hover": { borderColor: "#DC2626", bgcolor: "#FEF2F2" },
              }}
            >
              {busy === "out" ? "Clocking out..." : "Clock Out"}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}
